import * as React from 'react'
import { values } from 'lodash'
import { renderCatch } from '../error/renderCatch'
import type { CommonDeps } from '../base/CommonDeps'
import type { PostTimeline, PostTimelineItem } from '../../../shared/domain/posts/types.no-deps'
import type { Profile } from '../user/Profile'
import {
    initialPromiseState,
    type PromiseState,
    promiseStateTrigger,
    promiseStateRender,
    promiseStateCancel,
} from '../util/PromiseState'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import { never } from '../util/never'
import { TimelineComment } from './TimelineComment'
import { TimelineEvent } from './TimelineEvent'
import type { PostEvent } from '../../../shared/domain/postEvents/types.no-deps'
import { getPostEvents, getComments } from '../../../shared/domain/posts/timeline.no-deps'
import type { RichTextComment } from '../../../shared/domain/postComments/types.no-deps'
import type { RichTextParagraph } from '../../../shared/domain/richText/types.no-deps'
import { Timeline } from '../ui/Timeline'
import { timelinePositionFromIndex } from '../ui/TimelineItem'
import { tryMarkNotificationsAsRead } from '../notifications/helpers'
import { elementDisplayName } from '../../../shared/domain/flows/element'
import { dictFromArray } from '../../../shared/lib/functional/dictionary.no-deps'

export type AfterTimelineItemComponent = React.ComponentType<{ item: PostTimelineItem<RichTextComment> }>

interface Props {
    deps: CommonDeps
    items: PostTimeline
    profiles: Promise<Profile[]>
    teamNames: { [teamId: number]: string }
    onCommentActionClick: (commentId: UUID) => void
    onEventClick?: (event: PostEvent) => void
    onCommentClick?: (comment: RichTextComment) => void
    onQuoteClick?: (comment: RichTextComment) => void
    isQuoteClickable?: (comment: RichTextComment) => boolean
    routeToUserPage?: (userId: number) => void
    editingCommentTextData?: {
        commentId: UUID
        onTextSave: (newValue: RichTextParagraph[]) => void
        onCancel: () => void
    }
    showContextMenu: boolean
    showVerticalLine?: boolean
    contextMenuData?: {
        commentId: UUID
        displayMenu: boolean
        onEdit: (commentId: string) => void
        onDelete: (commentId: string) => void
    }
    userId: number
    markNotificationsAsRead?: boolean
    syncNotifications?: () => Promise<void>
    featureFlags: {
        reportCommentsTranslation: boolean
    }
    fetchTranslation?: (comment: RichTextComment) => Promise<RichTextComment>
    footerContent?: React.ReactElement

    AfterItemContent?: AfterTimelineItemComponent
}

interface State {
    profilesState: PromiseState<{ [userId: number]: Profile }>
    isOnline: boolean
}

export class CommentTimeline extends React.Component<Props, State> {
    public state: State = {
        profilesState: initialPromiseState,
        isOnline: true,
    }

    private markAsReadTimeout: number

    public componentDidMount = async () => {
        promiseStateTrigger(this.props.deps, this, 'profilesState', async () => {
            return this.getProfiles()
        })

        if (this.props.markNotificationsAsRead) {
            // Wait for the user to be on the page for 0.5 seconds before mark notifications as read
            // Otherwise timeout will be canceled by componentWillUnmount
            this.markAsReadTimeout = window.setTimeout(this.markNotificationsAsRead, 500)
        }

        this.setState({ isOnline: await this.props.deps.isOnline() })
    }

    private markNotificationsAsRead = async () => {
        const { items, deps } = this.props

        const comments = getComments(items).map((c) => c.id)
        const postEvents = getPostEvents(items).map((e) => e.id)

        await tryMarkNotificationsAsRead(deps, { comments, postEvents }, this.props.syncNotifications)
    }

    private getProfiles = async () => {
        const profiles = await this.props.profiles
        return dictFromArray(profiles, (p) => p.userId.toString())
    }

    public componentWillUnmount = () => {
        promiseStateCancel(this.state.profilesState)
        clearTimeout(this.markAsReadTimeout)
    }

    public render = () => renderCatch(this.props.deps, this.tryRender)

    private renderItems = (profileMap: { [userId: number]: Profile }) => {
        const {
            items,
            deps,
            onCommentActionClick,
            editingCommentTextData,
            onEventClick,
            onCommentClick,
            fetchTranslation,
            featureFlags,
            showVerticalLine,

            AfterItemContent,
        } = this.props
        return items.map((item, index) => {
            const position = timelinePositionFromIndex(index, items.length)
            const canTranslateComments = this.state.isOnline && featureFlags.reportCommentsTranslation

            switch (item.type) {
                case 'comment': {
                    return (
                        <>
                            <TimelineComment
                                key={item.comment.id}
                                deps={deps}
                                comment={item.comment}
                                quote={item.quotedElement && elementDisplayName(item.quotedElement)}
                                position={position}
                                profile={profileMap[item.comment.createdBy]}
                                onActionClick={onCommentActionClick}
                                editingCommentTextData={editingCommentTextData}
                                onClick={onCommentClick}
                                onQuoteClick={
                                    this.props.isQuoteClickable &&
                                    this.props.isQuoteClickable(item.comment) &&
                                    this.props.onQuoteClick &&
                                    (() => this.props.onQuoteClick(item.comment))
                                }
                                routeToUserPage={this.props.routeToUserPage}
                                showContextMenu={this.props.showContextMenu}
                                contextMenuData={this.props.contextMenuData}
                                currentUserId={this.props.userId}
                                profiles={values(profileMap)}
                                getProfile={(userId: number) => profileMap[userId]}
                                fetchTranslation={fetchTranslation}
                                allowTranslation={canTranslateComments}
                                showVerticalLine={showVerticalLine}
                                mentionsBelow={index === 0}
                            />
                            {AfterItemContent && <AfterItemContent item={item} />}
                        </>
                    )
                }
                case 'event':
                    return (
                        <TimelineEvent
                            key={item.event.id}
                            deps={deps}
                            event={item.event}
                            position={position}
                            getProfile={(userId: number) => profileMap[userId]}
                            getTeamName={(teamId: number) => this.props.teamNames[teamId]}
                            onClick={onEventClick}
                            routeToUserPage={this.props.routeToUserPage}
                        />
                    )
                default:
                    never(item)
            }
        })
    }

    private tryRender = () => {
        const { deps, footerContent } = this.props
        const { profilesState } = this.state
        return (
            <div className="AppCore__commentTimeline__CommentTimeline">
                {promiseStateRender(deps, profilesState, (profileMap) => (
                    <Timeline>{this.renderItems(profileMap)}</Timeline>
                ))}
                {footerContent}
            </div>
        )
    }
}
